/**
 * Separates Sentry error reports from optional measurements and session recordings.
 * Checks current choices before sending optional data and cancels requests on withdrawal.
 */
import { makeFetchTransport } from "@sentry/vue"
import { TrackingPurpose, type PrivacyChoices } from "@/types/privacyChoices"
import { isPrivateRoute } from "@/utils/trackingLocation"

type SentryTransportOptions = Parameters<typeof makeFetchTransport>[0]
type SentryTransport = ReturnType<typeof makeFetchTransport>

const activeOptionalRequests = new Set<AbortController>()
let optionalTelemetryStopped = false

/** Old SDK work must not resume if permission is granted again before the page refreshes. */
export function stopOptionalSentryTelemetry(): void {
	optionalTelemetryStopped = true
	for (const controller of activeOptionalRequests) controller.abort()
}

export function createPrivacySentryTransport(
	options: SentryTransportOptions,
	getChoices: () => PrivacyChoices
): SentryTransport {
	const nativeFetch = window.fetch.bind(window)
	const diagnosticsTransport = makeFetchTransport(options)

	function allowed(requiredPurposes: TrackingPurpose[]): boolean {
		if (optionalTelemetryStopped) return false
		const choices = getChoices()
		return !isPrivateRoute() && requiredPurposes.every(purpose => choices[purpose] === true)
	}

	function optionalTransport(requiredPurposes: TrackingPurpose[]): SentryTransport {
		return makeFetchTransport(options, (url, request) => {
			// Check again at dispatch, after the SDK has serialized the envelope.
			if (!allowed(requiredPurposes)) return Promise.resolve(new Response(null, { status: 204 }))
			const controller = new AbortController()
			const existingSignal = request?.signal
			const abort = () => controller.abort()
			if (existingSignal?.aborted) abort()
			else existingSignal?.addEventListener("abort", abort, { once: true })
			activeOptionalRequests.add(controller)
			return nativeFetch(url, { ...request, keepalive: false, signal: controller.signal })
				.catch(error => {
					// Privacy withdrawal intentionally consumes the SDK's pending request.
					if (optionalTelemetryStopped) return new Response(null, { status: 204 })
					throw error
				})
				.finally(() => {
					activeOptionalRequests.delete(controller)
					existingSignal?.removeEventListener("abort", abort)
				})
		})
	}

	const usageTransport = optionalTransport([TrackingPurpose.UsageStatistics])
	const replayTransport = optionalTransport([TrackingPurpose.SessionRecording])
	const combinedTransport = optionalTransport([TrackingPurpose.UsageStatistics, TrackingPurpose.SessionRecording])

	return {
		send(envelope) {
			const requiresUsage = envelope[1].some(([header]) =>
				["transaction", "profile", "span", "session", "sessions"].includes(header.type)
			)
			const requiresReplay = envelope[1].some(([header]) => ["replay_event", "replay_recording"].includes(header.type))
			if (!requiresUsage && !requiresReplay) return diagnosticsTransport.send(envelope)
			const requiredPurposes = [
				...(requiresUsage ? [TrackingPurpose.UsageStatistics] : []),
				...(requiresReplay ? [TrackingPurpose.SessionRecording] : []),
			]
			if (!allowed(requiredPurposes)) return Promise.resolve()
			const transport =
				requiresUsage && requiresReplay ? combinedTransport : requiresUsage ? usageTransport : replayTransport
			return transport.send(envelope)
		},
		async flush(timeout) {
			const results = await Promise.all([
				diagnosticsTransport.flush(timeout),
				usageTransport.flush(timeout),
				replayTransport.flush(timeout),
				combinedTransport.flush(timeout),
			])
			return results.every(Boolean)
		},
	}
}
